import React, { useEffect, useState } from "react";
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { LAW_IMPLEMENTATION_REQUEST_STATUS, LEGAL_ADVISORY_REQUEST_STATUS } from "utils/constantValues";
import { sub } from "date-fns";
import { useTranslation } from "react-i18next";

// Define the Topic type (same as in your parent)
export interface Topic {
    id: string;
    sector_name: string;
    priority: number | null;
    is_slected_to_audit: boolean;
    is_approved: boolean;
    created_at: string;
    updated_at: string;
    follow_up: string | null;
    audit_follow_up: string;
    followup_team?: string | null;
}

interface TopicListProps {
    formState: Topic[];
    setFormState: React.Dispatch<React.SetStateAction<Topic[]>>;
    status: string;
    edit: boolean;
    teams?: any[];
    forwardStatus?: string;
    onSubmitAccepted?: () => void;
}

export default function TargetList({ formState, setFormState, status, edit, teams, forwardStatus, onSubmitAccepted }: TopicListProps) {
    const { t } = useTranslation();
    const sensors = useSensors(useSensor(PointerSensor));
    const contextData = useAuth();
    const [isCollapsed, setIsCollapsed] = useState(false);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setFormState((prev: any) => {
            const topics = [...(prev.identified_law_sectors || [])];

            const oldIndex = topics.findIndex((t) => t.id === active.id);
            const newIndex = topics.findIndex((t) => t.id === over.id);

            if (oldIndex === -1 || newIndex === -1) return prev;

            const reordered = arrayMove(topics, oldIndex, newIndex).map((t, i) => ({
                ...t,
                priority: i + 1,
            }));

            return { ...prev, identified_law_sectors: reordered };
        });
    };
    
    const [submitted, setSubmitted] = useState((formState.find((data: any) => data.is_slected_to_audit)?.is_slected_to_audit
        && !(contextData?.user?.roles?.some((p) => p === "GeneralAttorney") || contextData?.user?.roles?.some((p) => p === "GeneralAttorney")))
        || edit);

    const toggleApproved = (id: string) => {
        setFormState((prev: any) => {
            const updated = prev.identified_law_sectors.map((topic: any) =>
                topic.id === id
                    ? { ...topic, is_approved: !topic.is_approved }
                    : topic
            );
            return { ...prev, identified_law_sectors: updated };
        });
    };
    
    const toggleAccepted = (id: string) => {
        setFormState((prev: any) => {
            const updated = prev.identified_law_sectors.map((topic: any) =>
                topic.id === id
                    ? { ...topic, is_slected_to_audit: !topic.is_slected_to_audit }
                    : topic
            );
            return { ...prev, identified_law_sectors: updated };
        });
    };

    // Filter topics based on user role
    let filteredTopics = [...formState];
    if (contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor")) {
        // For prosecutors, show only sectors from their team
        const userTeam = teams?.find(team =>
            team.prosecutors?.some((p: any) => p.user_id === contextData.user.user_id)
        );
        if (userTeam) {
            filteredTopics = filteredTopics.filter(topic =>
                userTeam.prosecutors?.some((p: any) => p.id.toString() === topic.followup_team?.toString())
            );
        } else {
            filteredTopics = []; // No team found, show nothing
        }
    }

    const sortedTopics = [...filteredTopics].sort((a, b) => {
        // Handle empty or missing priorities gracefully
        if (a.priority == null) return 1;
        if (b.priority == null) return -1;

        // Convert to number if priorities are stored as strings
        return Number(a.priority) - Number(b.priority);
    });
    
    const acceptedTopics = [...filteredTopics]
        .filter((t) => t.is_slected_to_audit)
        .sort((a, b) => {
            if (a.priority == null) return 1;
            if (b.priority == null) return -1;
            return Number(a.priority) - Number(b.priority);
        });
    
    // Show accepted list prominently for coordinators
    const showAcceptedList = contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") || 
                             contextData?.user?.roles?.some((p) => p?.toLowerCase() === "zonallif");

    const topicsToRender = contextData?.user?.roles?.some(
        (p) => p === "OAGBManagementHead"
    )
        ? acceptedTopics
        : sortedTopics;

    return (
        <div className="mt-2">
            {topicsToRender.length > 0 && (
                <div>
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="w-full flex items-center justify-between text-[#000000] text-xl font-bold mb-4 border-b-2 border-[#d1d5db] pb-2 hover:bg-[#f5f5f5] p-2 rounded"
                    >
                        <span>{t("IdentifiedLawSectors", { count: topicsToRender.length }) || `Identified Law Sectors (${topicsToRender.length})`}</span>
                        {isCollapsed ? <ChevronDown className="w-6 h-6" /> : <ChevronUp className="w-6 h-6" />}
                    </button>

                    {/* Show Accepted List Prominently for Coordinators */}
                    {showAcceptedList && acceptedTopics.length > 0 && (
                        <div className="mb-4 p-4 bg-green-50 border border-green-300 rounded-lg">
                            <p className="text-green-800 font-semibold mb-2">
                                {t("AcceptedTargets", { count: acceptedTopics.length }) || `Accepted Targets (${acceptedTopics.length}):`}
                            </p>
                            <div className="space-y-1">
                                {acceptedTopics.map((topic) => (
                                    <p key={topic.id} className="text-green-700 text-sm">
                                        ✓ {topic.sector_name}
                                    </p>
                                ))}
                            </div>
                        </div>
                    )}

                    {!isCollapsed && (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={topicsToRender.map((t) => t.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {topicsToRender.map((topic, index) => (
                                    <SortableItem
                                        key={topic.id}
                                        id={topic.id}
                                        topic={topic}
                                        index={index}
                                        status={status}
                                        edit={edit}
                                        submitted={submitted}
                                        toggleAccepted={toggleAccepted}
                                        toggleApproved={toggleApproved}
                                        teams={teams}
                                        forwardStatus={forwardStatus}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}

                </div>
            )}
        </div>
    );
}

interface SortableItemProps {
    id: string;
    topic: Topic;
    index: number;
    status: string;
    edit: boolean;
    submitted: boolean;
    toggleAccepted: (id: string) => void;
    toggleApproved: (id: string) => void;
    teams?: any[];
    forwardStatus?: string;
}

function SortableItem({ id, topic, index, toggleAccepted, toggleApproved, status, edit, submitted, teams, forwardStatus }: SortableItemProps) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });

    const contextData = useAuth();
    const navigate = useNavigate();
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    
    useEffect(() => { console.log("submitted: ", submitted) }, [edit, submitted]);

    // Find the team name for this topic - only for coordinators
    const isCoordinator = contextData.user.roles?.some((role: any) => role === "LegalResearchCoordinator") || contextData.user.roles?.some((role: any) => role?.toLowerCase() === "zonallif");
    const teamName = isCoordinator ? (teams?.find(team =>
        team.prosecutors?.some((p: any) => p.id.toString() === topic.followup_team?.toString())
    )?.team_name || t("UnknownTeam") || "Unknown Team") : null;

    // The director can accept / not-accept targets directly while the request is with them.
    const isDirectorEditable =
        contextData?.user?.roles?.some((p: any) => p === "LRDIFUDDirector") &&
        forwardStatus === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value &&
        status === LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex gap-4 items-center justify-between bg-white shadow-lg rounded-lg p-4 mb-4 border ${topic.is_slected_to_audit ? 'border-green-500 bg-green-50' : 'border-[#d1d5db] hover:bg-[#f5f5f5]'} transition-colors duration-200`}
        >
            <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${topic.is_slected_to_audit ? 'bg-green-500' : 'bg-[#8B0000]'}`}></div>
                <div>
                    <p className={`font-semibold text-base ${topic.is_slected_to_audit ? 'text-green-800' : 'text-[#000000]'}`}>{topic.sector_name}</p>
                    {isCoordinator && teamName && <p className="text-sm text-[#333333] mt-1">{t("SubmittedBy") || "Submitted by"}: {teamName}</p>}
                    {topic.is_slected_to_audit && <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">✓ {t("Accepted") || "Accepted"}</span>}
                </div>
            </div>

            {(((contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") || contextData?.user?.roles?.some((p) => p?.toLowerCase() === "zonallif")) &&
              forwardStatus && (forwardStatus === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value || forwardStatus === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value)) ||
            (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") &&
              forwardStatus && (forwardStatus === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value ||
                                forwardStatus === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value)) ||
            (contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
              forwardStatus === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value)) &&
            status === LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value &&
                <label className="ml-auto flex items-center gap-2">
                    {((status === LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value && !submitted) || edit || isDirectorEditable) &&
                        <input
                            type="checkbox"
                            checked={topic.is_slected_to_audit}
                            onChange={() => toggleAccepted(topic.id)}
                            className="ml-auto h-4 w-4 text-blue-600"
                        />
                    }

                    {(((status === LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value || edit) && !submitted) || isDirectorEditable) ?
                        <span className="text-sm">{t("Accept") || "Accept"}</span> :
                        <>
                            {
                                topic.is_slected_to_audit ?
                                    <span className="text-sm font-semibold text-green-600">{t("Accepted") || "Accepted"}</span> :
                                    <span className="text-sm text-red-500">{t("NotAccepted") || "Not Accepted"}</span>
                            }
                        </>
                    }
                </label>
            }
            <label className="gap-2">
                {topic.is_slected_to_audit &&
                    (status === LAW_IMPLEMENTATION_REQUEST_STATUS.identification_complete.value && forwardStatus === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value ?
                        <button onClick={() => topic.audit_follow_up && navigate(`/law-implementation/audit/${topic.audit_follow_up}`)} className="ml-auto px-4 py-2 bg-red-700 text-white rounded hover:bg-red-800">
                            {t("ViewAudit") || "View Audit"}
                        </button> :
                        <svg onClick={() => topic.audit_follow_up && navigate(`/law-implementation/audit/${topic.audit_follow_up}`)} className="mr-2 ml-auto my-auto cursor-pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <mask id="mask0_9_31087" maskUnits="userSpaceOnUse" x="1" y="5" width="22" height="14">
                                <path d="M12 18C17.523 18 22 12 22 12C22 12 6.477 18 12 18Z" fill="white" stroke="white" strokeWidth="1.33333" strokeLinejoin="round" />
                                <path d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z" fill="black" stroke="black" strokeWidth="1.33333" strokeLinejoin="round" />
                            </mask>
                            <g mask="url(#mask0_9_31087)">
                                <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                            </g>
                        </svg>
                    )
                }
            </label>
        </div>
    );
}